import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../utils/translations';
import { 
  Calendar, 
  Clock, 
  Users, 
  Building, 
  Mail, 
  Phone, 
  CheckCircle, 
  ArrowRight,
  Play,
  Star,
  Shield,
  Zap
} from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import SEOHead from './SEOHead';
import { getBreadcrumbStructuredData, getServiceStructuredData } from '../utils/structuredData';
import { supabase } from '../lib/supabase';

const DemoRequest = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    industry: '',
    employees: '',
    modules: [] as string[],
    preferredDate: '',
    preferredTime: '',
    message: '',
    website_url: '' // Honeypot field
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const breadcrumbData = getBreadcrumbStructuredData([
    { name: 'Home', url: 'https://laimu.mx/' },
    { name: 'Request Demo', url: 'https://laimu.mx/demo' }
  ]);

  const serviceStructuredData = getServiceStructuredData();

  const seoTitle = language === 'en' 
    ? 'Request Demo - LAIMU Trantor | Schedule Your Personalized Software Demo'
    : 'Solicitar Demo - LAIMU Trantor | Programa Tu Demo Personalizada de Software';
    
  const seoDescription = language === 'en'
    ? 'Schedule a free personalized demo of LAIMU Trantor\'s business management platform. See how our ERP, CRM, TMS, MRP, HRMS, and KPI solutions can transform your business operations.'
    : 'Programa una demo personalizada gratuita de la plataforma de gestión empresarial LAIMU Trantor. Ve cómo nuestras soluciones ERP, CRM, TMS, MRP, HRMS y KPI pueden transformar las operaciones de tu negocio.';

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleModuleChange = (moduleKey: string) => {
    setFormData(prev => ({
      ...prev,
      modules: prev.modules.includes(moduleKey)
        ? prev.modules.filter(m => m !== moduleKey)
        : [...prev.modules, moduleKey]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const { data, error } = await supabase.functions.invoke('submit-lead', {
        body: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          industry: formData.industry,
          employees: formData.employees,
          message: `${formData.message}\n\nFecha Preferida: ${formData.preferredDate || 'No especificada'}\nHora Preferida: ${formData.preferredTime || 'No especificada'}`,
          modules: formData.modules,
          website_url: formData.website_url
        }
      });

      if (error) {
        throw new Error(error.message || 'Error submitting demo request');
      }

      setIsSubmitted(true);
    } catch (err: any) {
      console.error('Demo request submission failed:', err);
      setSubmitError(
        language === 'en'
          ? 'There was a problem submitting your request. Please check your connection and try again.'
          : 'Hubo un problema al enviar tu solicitud. Por favor verifica tu conexión e intenta de nuevo.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gray-50">
        <SEOHead 
          title={language === 'en' ? 'Demo Request Submitted - LAIMU Trantor' : 'Solicitud de Demo Enviada - LAIMU Trantor'}
          description={language === 'en' ? 'Your demo request has been submitted successfully. Our team will contact you soon.' : 'Tu solicitud de demo ha sido enviada exitosamente. Nuestro equipo se pondrá en contacto contigo pronto.'}
          url="/demo"
          noIndex={true}
        />
        <Header />
        
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-2xl mx-auto text-center">
              <div className="bg-white rounded-xl shadow-lg p-8 md:p-12">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  {language === 'en' ? 'Demo Request Submitted!' : '¡Solicitud de Demo Enviada!'}
                </h2>
                <p className="text-xl text-gray-600 mb-6">
                  {language === 'en' 
                    ? 'Thank you for your interest in LAIMU Trantor. Our team will contact you within 24 hours to schedule your personalized demo.'
                    : 'Gracias por tu interés en LAIMU Trantor. Nuestro equipo se pondrá en contacto contigo en las próximas 24 horas para programar tu demo personalizada.'
                  }
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <p className="text-blue-800">
                    <strong>{language === 'en' ? 'What happens next?' : '¿Qué sigue?'}</strong>
                  </p>
                  <ul className="text-blue-700 text-sm mt-2 space-y-1">
                    <li>• {language === 'en' ? 'Our sales team will review your requirements' : 'Nuestro equipo de ventas revisará tus requerimientos'}</li>
                    <li>• {language === 'en' ? 'We\'ll prepare a customized demo based on your industry' : 'Prepararemos una demo personalizada basada en tu industria'}</li>
                    <li>• {language === 'en' ? 'You\'ll receive a calendar invite for the demo session' : 'Recibirás una invitación de calendario para la sesión de demo'}</li>
                  </ul>
                </div>
                <button
                  onClick={() => setIsSubmitted(false)}
                  className="px-6 py-3 text-white rounded-md transition-colors font-medium"
                  style={{ backgroundColor: '#115d7b' }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#0f5269'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#115d7b'}
                >
                  {language === 'en' ? 'Request Another Demo' : 'Solicitar Otra Demo'}
                </button>
              </div>
            </div>
          </div>
        </section>
        
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead 
        title={seoTitle}
        description={seoDescription}
        url="/demo"
        structuredData={[breadcrumbData, serviceStructuredData]}
        keywords={language === 'en' 
          ? 'demo request, software demo, ERP demo, CRM demo, business software trial, personalized demo, LAIMU demo, free consultation'
          : 'solicitar demo, demo software, demo ERP, demo CRM, prueba software empresarial, demo personalizada, demo LAIMU, consulta gratuita'
        }
      />
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-blue-50 via-white to-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <div className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium mb-6" style={{ backgroundColor: '#e0f2fe', color: '#115d7b' }}>
              <Calendar className="w-4 h-4 mr-2" />
              {language === 'en' ? 'Free Personalized Demo' : 'Demo Personalizada Gratuita'}
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              {language === 'en' ? 'Request Your Personalized Demo' : 'Solicita Tu Demo Personalizada'}
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              {language === 'en' 
                ? 'See LAIMU Trantor in action with a customized demonstration tailored to your industry and business needs.'
                : 'Ve LAIMU Trantor en acción con una demostración personalizada adaptada a tu industria y necesidades de negocio.'
              }
            </p>
          </div>

          {/* Benefits Bar */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 max-w-4xl mx-auto">
            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Play className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">
                {language === 'en' ? 'Live Demo' : 'Demo en Vivo'}
              </h3>
              <p className="text-gray-600 text-sm">
                {language === 'en' ? '45-minute interactive session' : 'Sesión interactiva de 45 minutos'}
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">
                {language === 'en' ? 'Industry Specific' : 'Específico por Industria'}
              </h3>
              <p className="text-gray-600 text-sm">
                {language === 'en' ? 'Tailored to your business sector' : 'Adaptado a tu sector empresarial'}
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">
                {language === 'en' ? 'No Commitment' : 'Sin Compromiso'}
              </h3>
              <p className="text-gray-600 text-sm">
                {language === 'en' ? 'Free consultation and demo' : 'Consulta y demo gratuitas'}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Demo Form */}
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  {language === 'en' ? 'Schedule Your Demo' : 'Programa Tu Demo'}
                </h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Personal Information */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {language === 'en' ? 'Full Name *' : 'Nombre Completo *'}
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:border-transparent"
                        style={{ '--tw-ring-color': '#115d7b' } as React.CSSProperties}
                        placeholder={language === 'en' ? 'John Doe' : 'Juan Pérez'}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {language === 'en' ? 'Email Address *' : 'Correo Electrónico *'}
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:border-transparent"
                        style={{ '--tw-ring-color': '#115d7b' } as React.CSSProperties}
                        placeholder={language === 'en' ? 'john@company.com' : 'juan@empresa.com'}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {language === 'en' ? 'Phone Number' : 'Número de Teléfono'}
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:border-transparent"
                        style={{ '--tw-ring-color': '#115d7b' } as React.CSSProperties}
                        placeholder="+52 55 1234 5678"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {language === 'en' ? 'Company Name *' : 'Nombre de la Empresa *'}
                      </label>
                      <input
                        type="text"
                        name="company"
                        required
                        value={formData.company}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:border-transparent"
                        style={{ '--tw-ring-color': '#115d7b' } as React.CSSProperties}
                        placeholder={language === 'en' ? 'Your Company' : 'Tu Empresa'}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {language === 'en' ? 'Industry *' : 'Industria *'}
                      </label>
                      <select
                        name="industry"
                        required
                        value={formData.industry}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:border-transparent"
                        style={{ '--tw-ring-color': '#115d7b' } as React.CSSProperties}
                      >
                        <option value="">{language === 'en' ? 'Select Industry' : 'Seleccionar Industria'}</option>
                        <option value="automotive">{language === 'en' ? 'Automotive' : 'Automotriz'}</option>
                        <option value="manufacturing">{language === 'en' ? 'Manufacturing' : 'Manufactura'}</option>
                        <option value="logistics">{language === 'en' ? 'Logistics' : 'Logística'}</option>
                        <option value="printing">{language === 'en' ? 'Printing & Packaging' : 'Impresión y Empaque'}</option>
                        <option value="other">{language === 'en' ? 'Other' : 'Otro'}</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {language === 'en' ? 'Company Size' : 'Tamaño de la Empresa'}
                      </label>
                      <select
                        name="employees"
                        value={formData.employees}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:border-transparent"
                        style={{ '--tw-ring-color': '#115d7b' } as React.CSSProperties}
                      >
                        <option value="">{language === 'en' ? 'Select Size' : 'Seleccionar Tamaño'}</option>
                        <option value="1-10">1-10 {language === 'en' ? 'employees' : 'empleados'}</option>
                        <option value="11-50">11-50 {language === 'en' ? 'employees' : 'empleados'}</option>
                        <option value="51-200">51-200 {language === 'en' ? 'employees' : 'empleados'}</option>
                        <option value="201-500">201-500 {language === 'en' ? 'employees' : 'empleados'}</option>
                        <option value="500+">{language === 'en' ? '500+ employees' : '500+ empleados'}</option>
                      </select>
                    </div>
                  </div>

                  {/* Modules of Interest */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      {language === 'en' ? 'Modules of Interest' : 'Módulos de Interés'}
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {Object.entries(t.solutions.modules).map(([key, module]) => (
                        <label key={key} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.modules.includes(key)}
                            onChange={() => handleModuleChange(key)}
                            className="mr-2 rounded"
                            style={{ accentColor: '#115d7b' }}
                          />
                          <span className="text-sm text-gray-700">{module.title}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Preferred Date and Time */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {language === 'en' ? 'Preferred Date' : 'Fecha Preferida'}
                      </label>
                      <input
                        type="date"
                        name="preferredDate"
                        value={formData.preferredDate}
                        onChange={handleInputChange}
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:border-transparent"
                        style={{ '--tw-ring-color': '#115d7b' } as React.CSSProperties}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {language === 'en' ? 'Preferred Time' : 'Hora Preferida'}
                      </label>
                      <select
                        name="preferredTime"
                        value={formData.preferredTime}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:border-transparent"
                        style={{ '--tw-ring-color': '#115d7b' } as React.CSSProperties}
                      >
                        <option value="">{language === 'en' ? 'Select Time' : 'Seleccionar Hora'}</option>
                        <option value="09:00">09:00 AM</option>
                        <option value="10:00">10:00 AM</option>
                        <option value="11:00">11:00 AM</option>
                        <option value="14:00">02:00 PM</option>
                        <option value="15:00">03:00 PM</option>
                        <option value="16:00">04:00 PM</option>
                      </select>
                    </div>
                  </div>

                  {/* Additional Message */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {language === 'en' ? 'Additional Information' : 'Información Adicional'}
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:border-transparent"
                      style={{ '--tw-ring-color': '#115d7b' } as React.CSSProperties}
                      placeholder={language === 'en' 
                        ? 'Tell us about your specific needs or questions...'
                        : 'Cuéntanos sobre tus necesidades específicas o preguntas...'
                      }
                    />
                  </div>

                  {/* Honeypot field - visually hidden for bot protection */}
                  <div className="absolute opacity-0 -z-10 select-none pointer-events-none w-0 h-0 overflow-hidden" aria-hidden="true">
                    <label htmlFor="website_url">Website URL</label>
                    <input
                      id="website_url"
                      type="text"
                      name="website_url"
                      value={formData.website_url}
                      onChange={handleInputChange}
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </div>

                  {submitError && (
                    <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-md animate-pulse">
                      {submitError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full px-6 py-4 text-white rounded-md transition-all font-medium text-lg flex items-center justify-center ${
                      isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
                    }`}
                    style={{ backgroundColor: '#115d7b' }}
                    onMouseEnter={(e) => {
                      if (!isSubmitting) {
                        e.currentTarget.style.backgroundColor = '#0f5269';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSubmitting) {
                        e.currentTarget.style.backgroundColor = '#115d7b';
                      }
                    }}
                  >
                    {isSubmitting 
                      ? (language === 'en' ? 'Sending Request...' : 'Enviando Solicitud...') 
                      : (language === 'en' ? 'Request Demo' : 'Solicitar Demo')}
                    {!isSubmitting && <ArrowRight className="ml-2 h-5 w-5" />}
                  </button>
                </form>
              </div>

              {/* Demo Information */}
              <div className="space-y-8">
                {/* What to Expect */}
                <div className="bg-white rounded-xl shadow-lg p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">
                    {language === 'en' ? 'What to Expect' : 'Qué Esperar'}
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-4 mt-1">
                        <span className="text-blue-600 font-bold text-sm">1</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">
                          {language === 'en' ? 'Personalized Walkthrough' : 'Recorrido Personalizado'}
                        </h4>
                        <p className="text-gray-600 text-sm">
                          {language === 'en' 
                            ? 'See how LAIMU Trantor works specifically for your industry and use cases.'
                            : 'Ve cómo funciona LAIMU Trantor específicamente para tu industria y casos de uso.'
                          }
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-4 mt-1">
                        <span className="text-blue-600 font-bold text-sm">2</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">
                          {language === 'en' ? 'Live Q&A Session' : 'Sesión de Preguntas en Vivo'}
                        </h4>
                        <p className="text-gray-600 text-sm">
                          {language === 'en' 
                            ? 'Ask questions and get immediate answers from our product experts.'
                            : 'Haz preguntas y obtén respuestas inmediatas de nuestros expertos en productos.'
                          }
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-4 mt-1">
                        <span className="text-blue-600 font-bold text-sm">3</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">
                          {language === 'en' ? 'Custom Proposal' : 'Propuesta Personalizada'}
                        </h4>
                        <p className="text-gray-600 text-sm">
                          {language === 'en' 
                            ? 'Receive a tailored implementation plan and pricing for your business.'
                            : 'Recibe un plan de implementación personalizado y precios para tu negocio.'
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Customer Testimonial */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-8 border border-blue-100">
                  <div className="flex items-center mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                  <blockquote className="text-gray-700 italic mb-4">
                    "{language === 'en' 
                      ? 'The demo was incredibly detailed and showed exactly how LAIMU Trantor would solve our specific challenges. The team was knowledgeable and patient with all our questions.'
                      : 'La demo fue increíblemente detallada y mostró exactamente cómo LAIMU Trantor resolvería nuestros desafíos específicos. El equipo fue conocedor y paciente con todas nuestras preguntas.'
                    }"
                  </blockquote>
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-gray-300 rounded-full mr-3"></div>
                    <div>
                      <div className="font-semibold text-gray-900">María González</div>
                      <div className="text-gray-600 text-sm">
                        {language === 'en' ? 'Operations Director, AutoMex' : 'Directora de Operaciones, AutoMex'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="bg-white rounded-xl shadow-lg p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    {language === 'en' ? 'Need Help?' : '¿Necesitas Ayuda?'}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {language === 'en' 
                      ? 'Have questions about the demo or need immediate assistance?'
                      : '¿Tienes preguntas sobre la demo o necesitas asistencia inmediata?'
                    }
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <Phone className="w-5 h-5 mr-3" style={{ color: '#115d7b' }} />
                      <a href="tel:+528111071450" className="text-gray-700 hover:text-blue-600">
                        +52 81 1107 1450
                      </a>
                    </div>
                    <div className="flex items-center">
                      <Mail className="w-5 h-5 mr-3" style={{ color: '#115d7b' }} />
                      <a href="mailto:demo@laimu.mx" className="text-gray-700 hover:text-blue-600">
                        demo@laimu.mx
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default DemoRequest;